/**
 * /slack-bot — public install + docs page for the @cartie Slack bot.
 *
 * v34.37 (Feb 2026): paired with /api/slack/cartie-command (slash) and
 * /api/slack/cartie-events (@mentions). Backend ships canned answers
 * for the 4 most common queries plus Claude Sonnet LLM fallback.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Hash, ArrowRight, Copy, Check, Slack, Zap, ShieldCheck, Sparkles, MessageSquare,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const SLACK_MANIFEST = `display_information:
  name: Cartie
  description: Ask your cloud costs in plain English — replies in 2 seconds.
  background_color: "#0b1120"
features:
  bot_user:
    display_name: Cartie
    always_online: true
  slash_commands:
    - command: /cartie
      url: https://cartieai.com/api/slack/cartie-command
      description: Ask Cartie a FinOps question
      usage_hint: "why is AWS up 14%"
oauth_config:
  scopes:
    bot:
      - app_mentions:read
      - chat:write
      - commands
settings:
  event_subscriptions:
    request_url: https://cartieai.com/api/slack/cartie-events
    bot_events:
      - app_mention
  interactivity:
    is_enabled: true
  org_deploy_enabled: false
  socket_mode_enabled: false`;

const EXAMPLES = [
  {
    invocation: '/cartie why is AWS up 14%?',
    persona: 'CTO · Monday morning',
    response: 'AWS is up 14% this week. Root causes: 41% idle EBS in us-east-1 · 24% oversized RDS prod-payments (18% util) · 18% cross-region replication from data-warehouse.',
    color: 'emerald',
  },
  {
    invocation: '@cartie top 3 recommendations',
    persona: 'FinOps lead · planning ritual',
    response: '1. Rightsize prod-payments RDS → $612/mo (91% conf) · 2. Stop dev-orphaned-rds-7 → $480/mo (96% conf) · 3. Spot-migrate staging-batch-ml → $410/mo (84% conf).',
    color: 'sky',
  },
  {
    invocation: '/cartie burning',
    persona: 'On-call engineer · 2am page',
    response: ':fire: Active burn: $4.20/hr from 14 unapplied recommendations. Tap the dashboard link to triage.',
    color: 'amber',
  },
  {
    invocation: '/cartie envelopes',
    persona: 'Eng manager · sprint review',
    response: ':shield: ml-platform 73% · payments-api 41% · marketing-rag 92% (review needed). File Overdrive: /cartie overdrive.',
    color: 'pink',
  },
];

const COLOR_MAP = {
  emerald: { ring: 'border-emerald-500/30', dot: 'bg-emerald-500', text: 'text-emerald-300' },
  sky: { ring: 'border-sky-500/30', dot: 'bg-sky-500', text: 'text-sky-300' },
  amber: { ring: 'border-amber-500/30', dot: 'bg-amber-500', text: 'text-amber-300' },
  pink: { ring: 'border-pink-500/30', dot: 'bg-pink-500', text: 'text-pink-300' },
};

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-400 hover:text-emerald-300 transition-colors"
      data-testid="slackbot-copy-btn"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const SlackBotPage = () => {
  const [stats, setStats] = useState({ total_invocations: null });

  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    fetch(`${url}/api/slack/cartie/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && typeof d.total_invocations === 'number') setStats(d); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
      <SEOHead
        title="@cartie Slack bot — Ask cloud costs in plain English | CARTIEAI"
        description="Slash command + @mentions. Claude Sonnet answers FinOps questions in <2s. Drop-in OAuth manifest. SOC 2-ready signing-secret verification."
      />
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-emerald-500/[0.08] blur-[140px]" />
        <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-rose-500/[0.05] blur-[120px]" />
      </div>

      <header className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="slackbot-nav-home">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-7 h-7 object-contain" />
            <span className="font-display font-black text-base tracking-tight">
              <span className="text-white">CARTIE</span><span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-emerald-300 inline-flex items-center gap-1">
            ← Back home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16 sm:py-24">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
            <Slack className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-200">@cartie bot · live</span>
            {stats.total_invocations !== null && (
              <span className="text-[11px] font-mono text-slate-400 border-l border-emerald-500/30 pl-2 ml-1" data-testid="slackbot-invocations">
                {stats.total_invocations.toLocaleString()} queries answered
              </span>
            )}
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-[-0.03em] leading-[1.05] mb-5">
            Ask your cloud costs{' '}
            {/* v34.60 — was bg-clip-text gradient ending in rose-400 which faded
                the last letters of "in Slack." to look like glare. Solid bright
                emerald + drop-shadow glow matches brand + stays fully readable. */}
            <span className="text-emerald-300 drop-shadow-[0_0_24px_rgba(16,185,129,0.45)]">
              in Slack.
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            <code className="font-mono text-emerald-300">/cartie why is AWS up 14%?</code> — a 3-bullet root-cause analysis lands in under two seconds. No dashboard tab, no copy-paste, no Snowflake query.
          </p>
        </div>

        {/* Example transcripts */}
        <h2 className="text-2xl font-bold text-white mb-4">What it answers</h2>
        <div className="space-y-3 mb-14">
          {EXAMPLES.map((e, i) => {
            const c = COLOR_MAP[e.color];
            return (
              <div key={i} className={`rounded-xl border ${c.ring} bg-slate-900/60 overflow-hidden`} data-testid={`slackbot-example-${i}`}>
                <div className="px-4 py-3 flex items-center justify-between gap-4 border-b border-white/5 bg-slate-950/30">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                    <code className="font-mono text-[13px] text-white">{e.invocation}</code>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{e.persona}</span>
                </div>
                <div className="px-4 py-3 flex gap-3">
                  <MessageSquare className={`w-4 h-4 mt-0.5 shrink-0 ${c.text}`} />
                  <p className="text-sm text-slate-300 leading-relaxed">{e.response}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Install — Slack manifest */}
        <h2 className="text-2xl font-bold text-white mb-2">Install in 3 minutes</h2>
        <p className="text-sm text-slate-400 mb-6">No vendor onboarding. Slack ships a manifest-based installer — paste the YAML below, click <em>Install to Workspace</em>, done.</p>
        <ol className="space-y-3 mb-6 text-sm text-slate-300">
          <li className="flex gap-3"><span className="text-emerald-400 shrink-0 font-mono">1</span> Open <a href="https://api.slack.com/apps?new_app=1" target="_blank" rel="noreferrer" className="text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline">api.slack.com/apps → Create New App</a>, choose <span className="text-white font-semibold">From a manifest</span>.</li>
          <li className="flex gap-3"><span className="text-emerald-400 shrink-0 font-mono">2</span> Paste the manifest below, pick your workspace, hit <span className="text-white font-semibold">Install to Workspace</span>.</li>
          <li className="flex gap-3"><span className="text-emerald-400 shrink-0 font-mono">3</span> Copy the <span className="text-white font-semibold">Bot User OAuth Token</span> and <span className="text-white font-semibold">Signing Secret</span> into your CARTIE env vars (<code className="text-emerald-300 font-mono">SLACK_BOT_TOKEN</code>, <code className="text-emerald-300 font-mono">SLACK_SIGNING_SECRET</code>).</li>
        </ol>

        <div className="rounded-2xl border border-emerald-500/30 bg-slate-900/60 overflow-hidden mb-14" data-testid="slackbot-manifest">
          <div className="px-4 py-2 border-b border-emerald-500/20 bg-slate-950/40 flex items-center justify-between">
            <span className="text-[11px] font-mono text-slate-400">slack manifest · yaml</span>
            <CopyButton text={SLACK_MANIFEST} label="Copy manifest" />
          </div>
          <pre className="px-5 py-5 font-mono text-[12.5px] text-emerald-200 overflow-x-auto leading-relaxed">
{SLACK_MANIFEST}
          </pre>
        </div>

        {/* Trust row */}
        <div className="grid md:grid-cols-3 gap-3 mb-14">
          <div className="rounded-xl border border-emerald-500/20 bg-slate-900/40 px-4 py-4">
            <ShieldCheck className="w-5 h-5 text-emerald-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">Signing-secret verified</div>
            <div className="text-xs text-slate-400">Every webhook hits an HMAC check. 5-minute timestamp window blocks replay attacks. SOC 2-ready.</div>
          </div>
          <div className="rounded-xl border border-sky-500/20 bg-slate-900/40 px-4 py-4">
            <Sparkles className="w-5 h-5 text-sky-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">Claude Sonnet</div>
            <div className="text-xs text-slate-400">Canned answers for the 4 most common queries; Claude Sonnet handles the long tail. Average latency &lt; 1.8s.</div>
          </div>
          <div className="rounded-xl border border-rose-500/20 bg-slate-900/40 px-4 py-4">
            <Hash className="w-5 h-5 text-rose-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">Zero data retention</div>
            <div className="text-xs text-slate-400">We log invocation count + channel name for ops. No prompt text, no answers, no PII stored beyond the slash log.</div>
          </div>
        </div>

        {/* Why */}
        <div className="rounded-xl border border-white/10 bg-slate-900/40 px-6 py-6 mb-14">
          <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-3">Why this exists</div>
          <ul className="space-y-2 text-sm text-slate-300 leading-relaxed">
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">1.</span> <span><strong className="text-white">Slack is the cost UI for half the org.</strong> Engineers, PMs, and finance live there — your dashboard doesn't.</span></li>
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">2.</span> <span><strong className="text-white">2-second answers beat 2-minute dashboards.</strong> The bot returns the same 3-bullet root-cause analysis you'd get from the Anomaly Autopsy engine.</span></li>
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">3.</span> <span><strong className="text-white">Audit-friendly.</strong> Every query is logged with channel + user so finance can prove "we asked, we acted".</span></li>
          </ul>
        </div>

        {/* CTA row */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <a href="https://api.slack.com/apps?new_app=1" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors" data-testid="slackbot-cta-install">
            <Slack className="w-4 h-4" />
            Add Cartie to Slack
            <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/cli" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-white/30 text-slate-200 font-semibold text-sm transition-colors" data-testid="slackbot-cta-cli">
            <Zap className="w-4 h-4" />
            Prefer the CLI? <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
};

export default SlackBotPage;
